import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import {
  applyRecharge,
  fetchCurrentRecharge,
  fetchRechargeAddress,
  fetchRechargeStatus,
  fetchWalletSummary,
  type RechargeOrder,
} from '@/api/wallet';
import { getToken } from '@/lib/auth-storage';

const presetAmounts = [
  { value: 50, label: '50 U', tag: '' },
  { value: 100, label: '100 U', tag: '推荐' },
  { value: 200, label: '200 U', tag: '' },
  { value: 500, label: '500 U', tag: '优惠' },
  { value: 1000, label: '1000 U', tag: '' },
  { value: 2000, label: '2000 U', tag: '' },
];

function isPositiveInteger(value: number) {
  return Number.isInteger(value) && value > 0;
}

export default function RechargePage() {
  const [selectedAmount, setSelectedAmount] = useState<number>(100);
  const [customAmount, setCustomAmount] = useState('');
  const [balance, setBalance] = useState(0);
  const [addressReady, setAddressReady] = useState(false);
  const [hasAddress, setHasAddress] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);
  const [order, setOrder] = useState<RechargeOrder | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [copied, setCopied] = useState<'amount' | 'address' | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [paid, setPaid] = useState(false);

  const parsedCustom = customAmount === '' ? null : Number(customAmount);
  const finalAmount = parsedCustom != null && !Number.isNaN(parsedCustom) ? parsedCustom : selectedAmount;
  const amountOk = isPositiveInteger(finalAmount);
  const loggedIn = !!getToken();

  const remainMs = order?.expireAt ? order.expireAt - now : 0;
  const expiredLocally = !!order && order.status === '0' && remainMs <= 0;
  const waiting = !!order && order.status === '0' && !expiredLocally;

  useEffect(() => {
    if (!loggedIn) {
      setNeedLogin(true);
      setAddressReady(true);
      return;
    }
    fetchWalletSummary()
      .then((res) => {
        if (res.code === 0 && res.data) {
          setBalance(Number(res.data.balance || 0));
        }
      })
      .catch(() => {});
    fetchRechargeAddress()
      .then((res) => {
        if (res.code === 0 && res.data) {
          setHasAddress(!!(res.data.address || '').trim());
        } else if (res.code === 401) {
          setNeedLogin(true);
        }
      })
      .catch(() => {})
      .finally(() => setAddressReady(true));
    fetchCurrentRecharge()
      .then((res) => {
        if (res.code === 0 && res.data && res.data.status === '0') {
          setOrder(res.data);
        }
      })
      .catch(() => {});
  }, [loggedIn]);

  useEffect(() => {
    if (!waiting) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [waiting]);

  useEffect(() => {
    if (!order?.applyNo || !waiting) return;
    let stopped = false;
    const poll = () => {
      fetchRechargeStatus(order.applyNo)
        .then((res) => {
          if (stopped || res.code !== 0 || !res.data) return;
          setOrder(res.data);
          if (res.data.status === '1') {
            setPaid(true);
            fetchWalletSummary()
              .then((summary) => {
                if (summary.code === 0 && summary.data) {
                  setBalance(Number(summary.data.balance || 0));
                }
              })
              .catch(() => {});
          }
        })
        .catch(() => {});
    };
    poll();
    const timer = window.setInterval(poll, 5000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [order?.applyNo, waiting]);

  const handleRecharge = async () => {
    if (!amountOk || !hasAddress || submitting) return;
    setSubmitting(true);
    setError('');
    setPaid(false);
    setCopied('');
    try {
      const res = await applyRecharge(finalAmount);
      if (res.code !== 0 || !res.data) {
        setError(res.msg || '创建充值订单失败');
        return;
      }
      setOrder(res.data);
      setNow(Date.now());
    } catch {
      setError('创建充值订单失败');
    } finally {
      setSubmitting(false);
    }
  };

  const copyText = async (text: string, kind: 'amount' | 'address') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
    } catch {
      setCopied('');
    }
  };

  const remainText = () => {
    const sec = Math.max(0, Math.floor(remainMs / 1000));
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f8]">
      <Navbar />
      <main className="flex-1 max-w-xl mx-auto w-full px-4 sm:px-6 py-8 pb-24 md:pb-8">

        <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
          <Link to="/account" className="hover:text-gray-700 transition-colors cursor-pointer">用户中心</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/wallet" className="hover:text-gray-700 transition-colors cursor-pointer">我的钱包</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-700 font-medium">充值</span>
        </div>

        <div className="bg-gray-900 rounded-2xl p-5 mb-5 flex items-center justify-between">
          <div>
            <div className="text-white/60 text-xs mb-1">当前余额</div>
            <div className="text-white text-2xl font-bold flex items-baseline gap-1">
              <span>{balance.toFixed(2)}</span>
              <span className="text-sm font-medium text-white/70">U</span>
            </div>
          </div>
          <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl">
            <i className="ri-wallet-3-line text-white text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">选择充值金额</h2>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {presetAmounts.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => { setSelectedAmount(item.value); setCustomAmount(''); setError(''); }}
                className={`relative py-3 rounded-xl border text-sm font-semibold transition-all cursor-pointer ${
                  selectedAmount === item.value && !customAmount
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-200 text-gray-700 hover:border-gray-400'
                }`}
              >
                {item.label}
                {item.tag && (
                  <span className={`absolute -top-2 -right-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    selectedAmount === item.value && !customAmount
                      ? 'bg-white text-gray-900'
                      : 'bg-gray-900 text-white'
                  }`}>
                    {item.tag}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="relative">
            <input
              type="number"
              value={customAmount}
              onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(0); setError(''); }}
              placeholder="输入自定义整数金额 (U)"
              min="1"
              step="1"
              className="w-full border border-gray-200 rounded-xl pl-4 pr-12 py-3 text-sm focus:outline-none focus:border-gray-400 transition-colors"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">U</span>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-5">
          <div className="flex items-start gap-2.5">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-amber-500" />
            </div>
            <div className="text-sm text-amber-700 leading-relaxed">
              {needLogin
                ? '请先登录后查看收款地址。'
                : !addressReady
                  ? '正在获取收款地址…'
                  : hasAddress
                    ? '请使用 TRC20 网络，按页面显示的精确金额原样转入。多转、少转或超时都不会自动到账。'
                    : '暂未设置收款地址，请稍后再试。'}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-500">充值金额</span>
            <span className="text-lg font-bold text-gray-900 flex items-baseline gap-1">
              {amountOk ? (
                <>
                  <span>{finalAmount}</span>
                  <span className="text-sm font-medium text-gray-500">U</span>
                </>
              ) : (
                '请输入整数金额'
              )}
            </span>
          </div>
          {needLogin ? (
            <Link
              to="/login?redirect=%2Frecharge"
              className="w-full py-3.5 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center"
            >
              登录后充值
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleRecharge}
              disabled={!addressReady || !hasAddress || !amountOk || submitting}
              className="w-full py-3.5 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <i className="ri-wallet-3-line text-base" />
              {!addressReady ? '加载中…' : !hasAddress ? '暂未设置收款地址' : submitting ? '正在生成…' : waiting ? '重新生成订单' : '立即充值'}
            </button>
          )}

          {error && <p className="text-xs text-red-500 text-center mt-3">{error}</p>}

          {paid && order?.status === '1' && (
            <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              已到账 {order.amount} U，余额已更新。
            </div>
          )}

          {order && order.status === '2' && (
            <div className="mt-5 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              该充值订单已拒绝。
            </div>
          )}

          {(waiting || expiredLocally || order?.status === '3') && order && (
            <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-gray-500">请在倒计时内按此金额转入</div>
                <div className={`text-sm font-semibold ${expiredLocally || order.status === '3' ? 'text-red-500' : 'text-gray-900'}`}>
                  {expiredLocally || order.status === '3' ? '已过期' : remainText()}
                </div>
              </div>
              <div className="text-xs text-gray-500 mb-1">请转入（TRC20 USDT）</div>
              <div className="text-lg font-bold text-gray-900 mb-2 break-all">{order.payAmount} U</div>
              <button
                type="button"
                onClick={() => copyText(order.payAmount, 'amount')}
                className="mb-3 w-full py-2.5 border border-gray-900 text-gray-900 rounded-xl text-sm font-semibold hover:bg-gray-900 hover:text-white transition-colors cursor-pointer"
              >
                {copied === 'amount' ? '金额已复制' : '复制金额'}
              </button>
              <div className="text-xs text-gray-500 mb-1">USDT 收款地址</div>
              <div className="break-all text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg px-3 py-2">
                {order.address}
              </div>
              <button
                type="button"
                onClick={() => copyText(order.address, 'address')}
                className="mt-3 w-full py-2.5 border border-gray-900 text-gray-900 rounded-xl text-sm font-semibold hover:bg-gray-900 hover:text-white transition-colors cursor-pointer"
              >
                {copied === 'address' ? '地址已复制' : '复制地址'}
              </button>
              {(expiredLocally || order.status === '3') && (
                <p className="text-xs text-gray-500 mt-3">订单已过期。如已转账请联系客服补单，或重新生成一笔新订单。</p>
              )}
            </div>
          )}
          <p className="text-xs text-gray-400 text-center mt-3">仅支持 TRC20，请勿转到其他网络或其他地址</p>
        </div>

      </main>
      <Footer />
    </div>
  );
}
